import * as React from 'react';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { sanitizeRichHtml } from './rich-html-sanitizer';

// =============================================================================
// rich-html-editor.tsx — Éditeur WYSIWYG MAISON, PARTAGÉ (0 dépendance tierce).
// =============================================================================
// PARTAGÉ (V-CATALOG-UX) : même composant pour l'éditeur de CGU (BC legal, DBU §2.9) ET le
// descriptif/missions du catalogue (BC catalog, DBU §2.4).
//
// ── DÉCISION DBU (rétablie, R24 · FC-24-1) ────────────────────────────────────────────
// DBU §2.7/§2.9 l.2030-2032 EXIGE un « Éditeur WYSIWYG intégré à la plateforme : saisie DIRECTE du
// contenu SANS DÉPENDANCE à un éditeur externe » + « Fonctions d'édition courantes : titres
// hiérarchisés, paragraphes, listes à puces et numérotées, gras, italique, soulignement, liens
// hypertextes, tables simples ». « Sans dépendance externe » = CONSTRUIRE le WYSIWYG EN INTERNE
// (pas de tiptap/slate/quill/lexical/prosemirror) — ce n'est PAS « pas de WYSIWYG ». La version
// antérieure (textarea de HTML source + iframe d'aperçu) INVERSAIT cette décision : l'admin devait
// TAPER `<strong>`/`<ul>` à la main. C'est ce faux-conforme que R24 corrige.
//
// APPROCHE 0-DÉPENDANCE : une zone `contentEditable` (role="textbox") est l'unique surface — ce qui
// est édité EST le rendu formaté (What-You-See-Is-What-You-Get). Une barre d'outils déclenche le
// BALISAGE via `document.execCommand` (API navigateur native, aucune librairie). L'admin ne saisit
// JAMAIS de balise.
//
// ── SÉCURITÉ (CRITIQUE) ───────────────────────────────────────────────────────────────
// Le contenu VIT désormais dans le DOM de l'admin (contentEditable), pas dans une iframe inerte. Un
// collage (paste) ou une commande peut y injecter du HTML dangereux → self-XSS. Défense en profondeur :
//   1. `onPaste` : on intercepte, on nettoie (`sanitizeRichHtml`) AVANT insertion — jamais de HTML brut
//      collé dans le DOM parent.
//   2. `onChange` émis vers le parent = TOUJOURS la sortie nettoyée (allowlist stricte, href http(s)
//      seul attribut, aucun `on*`/`javascript:`).
//   3. Le serveur reste le choke point AUTORITAIRE au write (`@jeko/shared-kernel`), même politique.
// Aucun `dangerouslySetInnerHTML` : l'initialisation du contentEditable passe par `ref.innerHTML` sur
// une valeur DÉJÀ nettoyée.

/** Une commande de la barre d'outils (mappée sur `document.execCommand`). */
interface ToolbarCommand {
  readonly key: string;
  /** Libellé accessible (aria-label + title). */
  readonly label: string;
  /** Contenu visible du bouton (icône textuelle ; le sens est porté par aria-label). */
  readonly glyph: React.ReactNode;
  /** Nom de commande `execCommand`. */
  readonly command: string;
  /** Argument optionnel de `execCommand` (ex. `<h1>` pour formatBlock). */
  readonly value?: string;
  /** Vrai si la commande est un état basculable (gras/italique/souligné) → aria-pressed. */
  readonly toggle?: boolean;
}

// Groupes de la barre d'outils — couvrent EXACTEMENT les fonctions DBU l.2032.
const HEADING_COMMANDS: readonly ToolbarCommand[] = [
  { key: 'h1', label: 'Titre 1', glyph: 'T1', command: 'formatBlock', value: 'h1' },
  { key: 'h2', label: 'Titre 2', glyph: 'T2', command: 'formatBlock', value: 'h2' },
  { key: 'h3', label: 'Titre 3', glyph: 'T3', command: 'formatBlock', value: 'h3' },
  { key: 'p', label: 'Paragraphe', glyph: '¶', command: 'formatBlock', value: 'p' },
];

const INLINE_COMMANDS: readonly ToolbarCommand[] = [
  { key: 'bold', label: 'Gras', glyph: <strong>G</strong>, command: 'bold', toggle: true },
  { key: 'italic', label: 'Italique', glyph: <em>I</em>, command: 'italic', toggle: true },
  {
    key: 'underline',
    label: 'Souligné',
    glyph: <span className="underline">S</span>,
    command: 'underline',
    toggle: true,
  },
];

const LIST_COMMANDS: readonly ToolbarCommand[] = [
  { key: 'ul', label: 'Liste à puces', glyph: '•', command: 'insertUnorderedList' },
  { key: 'ol', label: 'Liste numérotée', glyph: '1.', command: 'insertOrderedList' },
];

// Gabarit d'insertion d'une table simple (2 colonnes, en-tête + une ligne). Sans attribut : conforme
// à l'allowlist (le sanitizer retire tout attribut de table de toute façon).
const TABLE_TEMPLATE =
  '<table><thead><tr><th>En-tête 1</th><th>En-tête 2</th></tr></thead>' +
  '<tbody><tr><td>Cellule</td><td>Cellule</td></tr></tbody></table><p><br></p>';

export interface RichHtmlEditorProps {
  /** Valeur courante du corps HTML (contrôlé). */
  readonly value: string;
  /** Notifie chaque modification (le parent détient l'état — pas de localStorage). Reçoit du HTML NETTOYÉ. */
  readonly onChange: (next: string) => void;
  /** Libellé du champ (associé programmatiquement à la zone d'édition). */
  readonly label: string;
  /** Texte d'aide sous le champ (associé via aria-describedby). */
  readonly hint: string;
  /** Désactive la saisie (ex. version non-Brouillon : lecture seule). */
  readonly disabled?: boolean;
  /** Id du champ pour l'association ARIA (les ids dérivés en découlent). */
  readonly id: string;
}

/** Applique une commande `execCommand` si l'API est disponible (absente en jsdom → no-op testable). */
function execCommand(command: string, value?: string): void {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    return;
  }
  document.execCommand(command, false, value);
}

/** Lit l'état basculable d'une commande (gras actif ?) si l'API est dispo, sinon `false`. */
function queryCommandState(command: string): boolean {
  if (typeof document === 'undefined' || typeof document.queryCommandState !== 'function') {
    return false;
  }
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

export function RichHtmlEditor({
  value,
  onChange,
  label,
  hint,
  disabled = false,
  id,
}: RichHtmlEditorProps): React.JSX.Element {
  const hintId = `${id}-hint`;
  const toolbarId = `${id}-toolbar`;
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  // Dernière valeur ÉMISE : évite de réécrire innerHTML (et de perdre le curseur) quand le parent
  // nous renvoie notre propre sortie. Une valeur EXTERNE différente (chargement/reset) resynchronise.
  const lastEmitted = React.useRef<string>(sanitizeRichHtml(value));
  const [activeStates, setActiveStates] = React.useState<Record<string, boolean>>({});
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');

  // Montage : injecte la valeur initiale NETTOYÉE (jamais de dangerouslySetInnerHTML). Ne référence
  // que des refs (deps vide légitime) ; la resynchro externe est gérée par l'effet dédié ci-dessous.
  React.useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = lastEmitted.current;
    }
  }, []);

  // Resynchronisation sur changement EXTERNE de `value` (différent de ce qu'on a émis).
  React.useEffect(() => {
    const clean = sanitizeRichHtml(value);
    if (clean !== lastEmitted.current && editorRef.current) {
      editorRef.current.innerHTML = clean;
      lastEmitted.current = clean;
    }
  }, [value]);

  const refreshToggleStates = React.useCallback(() => {
    setActiveStates({
      bold: queryCommandState('bold'),
      italic: queryCommandState('italic'),
      underline: queryCommandState('underline'),
    });
  }, []);

  const emitChange = React.useCallback(() => {
    const raw = editorRef.current?.innerHTML ?? '';
    const clean = sanitizeRichHtml(raw);
    lastEmitted.current = clean;
    onChange(clean);
  }, [onChange]);

  const runCommand = React.useCallback(
    (command: string, commandValue?: string) => {
      if (disabled) {
        return;
      }
      editorRef.current?.focus();
      execCommand(command, commandValue);
      refreshToggleStates();
      emitChange();
    },
    [disabled, emitChange, refreshToggleStates],
  );

  const insertTable = React.useCallback(() => {
    if (disabled) {
      return;
    }
    editorRef.current?.focus();
    execCommand('insertHTML', TABLE_TEMPLATE);
    emitChange();
  }, [disabled, emitChange]);

  const confirmLink = React.useCallback(() => {
    const trimmed = linkUrl.trim();
    setLinkOpen(false);
    setLinkUrl('');
    if (trimmed.length === 0) {
      return;
    }
    // Le sanitizer est le garde final (schéma http(s) uniquement) ; on ne présume rien ici.
    runCommand('createLink', trimmed);
  }, [linkUrl, runCommand]);

  const buttonClass =
    'inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-input bg-background px-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 aria-pressed:bg-accent aria-pressed:text-accent-foreground';

  function renderCommandButton(cmd: ToolbarCommand): React.JSX.Element {
    const pressed = cmd.toggle ? (activeStates[cmd.key] ?? false) : undefined;
    return (
      <button
        key={cmd.key}
        type="button"
        onClick={() => runCommand(cmd.command, cmd.value)}
        disabled={disabled}
        aria-label={cmd.label}
        title={cmd.label}
        aria-pressed={pressed}
        className={buttonClass}
      >
        {cmd.glyph}
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>

      {!disabled ? (
        <div
          role="toolbar"
          id={toolbarId}
          aria-label={`Mise en forme — ${label}`}
          aria-controls={id}
          // `sticky` : la barre reste visible pendant qu'on fait défiler un document long.
          // Sans elle, l'admin devait remonter en haut de page pour mettre en gras un mot
          // situé en bas — relevé Gate Human du 2026-09-15.
          className="sticky top-0 z-10 flex flex-wrap items-center gap-1 rounded-md border border-border bg-muted/40 p-1 backdrop-blur supports-[backdrop-filter]:bg-muted/70"
        >
          {HEADING_COMMANDS.map(renderCommandButton)}
          <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
          {INLINE_COMMANDS.map(renderCommandButton)}
          <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
          {LIST_COMMANDS.map(renderCommandButton)}
          <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
          <button
            type="button"
            onClick={() => setLinkOpen((v) => !v)}
            disabled={disabled}
            aria-label="Insérer un lien"
            aria-expanded={linkOpen}
            title="Insérer un lien"
            className={buttonClass}
          >
            🔗
          </button>
          <button
            type="button"
            onClick={insertTable}
            disabled={disabled}
            aria-label="Insérer une table"
            title="Insérer une table"
            className={buttonClass}
          >
            ▦
          </button>
        </div>
      ) : null}

      {linkOpen && !disabled ? (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-background p-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor={`${id}-link-url`}>Adresse du lien (http/https)</Label>
            <input
              id={`${id}-link-url`}
              type="url"
              inputMode="url"
              placeholder="https://exemple.fr"
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  confirmLink();
                }
              }}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            />
          </div>
          <button
            type="button"
            onClick={confirmLink}
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            Appliquer le lien
          </button>
        </div>
      ) : null}

      <div
        ref={editorRef}
        id={id}
        role="textbox"
        tabIndex={0}
        aria-multiline="true"
        aria-label={label}
        aria-describedby={hintId}
        aria-readonly={disabled}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        onInput={() => {
          emitChange();
        }}
        onKeyUp={refreshToggleStates}
        onMouseUp={refreshToggleStates}
        onFocus={refreshToggleStates}
        onPaste={(event) => {
          if (disabled) {
            return;
          }
          // NEUTRALISATION du collage : on nettoie AVANT insertion (jamais de HTML brut dans le DOM).
          event.preventDefault();
          const clipboard = event.clipboardData;
          const html = clipboard.getData('text/html');
          const text = clipboard.getData('text/plain');
          const safe = html.length > 0 ? sanitizeRichHtml(html) : sanitizeRichHtml(text);
          execCommand('insertHTML', safe);
          emitChange();
        }}
        className={cn(
          'max-w-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
          // `overflow-auto` ne défilait JAMAIS : sans plafond de hauteur, la zone grandissait
          // avec le texte, et c'est la PAGE qui défilait — emportant la barre d'outils hors
          // de l'écran. Le plafond est ce qui arme le défilement de la zone elle-même.
          'min-h-[16rem] max-h-[60vh] overflow-y-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          '[&_h1]:text-xl [&_h1]:font-semibold [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:text-base [&_h3]:font-semibold',
          '[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-primary [&_a]:underline',
          '[&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1',
          '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
          disabled ? 'cursor-not-allowed opacity-70' : null,
        )}
      />

      <p id={hintId} className="text-xs text-muted-foreground">
        {hint}
      </p>
    </div>
  );
}
